<template>
  <div class="relative">
    <ul
      :id="'carousel_' + safeId"
      ref="carousel"
      class="flex h-full relative"
      :class="[isScrollable ? ['carousel-scrollable', 'overflow-x-auto', 'whitespace-nowrap'] : '']"
    >
      <!-- slides -->
      <li
        v-for="(image, index) in images"
        :key="index"
        :data-index="index"
        class="carousel-image overflow-hidden w-full shrink-0"
        tabindex="-1"
      >
        <img
          :src="optimiseImg(image)"
          class="w-full h-full object-cover object-center"
          alt="Carousel image"
          :loading="(isFirst && index === 0) ? 'eager' : 'lazy'"
        >
      </li>
    </ul>
    <!-- prev button -->
    <button
      v-if="
        showControls
          &&
          isScrollable"
      type="button"
      aria-label="Previous image"
      class="carousel-control absolute bg-white flex items-center justify-center rounded-full"
      :class="[currentImageIndex === 0 ? ['opacity-0', 'pointer-events-none'] : '']"
      :tabindex="currentImageIndex === 0 ? '-1' : '0'"
      @click="previous()"
    >
      <MajorityUiMajorityIcon
        class="leading-0"
        icon="chevron-left"
      />
    </button>
    <!-- next button -->
    <button
      v-if="showControls && isScrollable"
      type="button"
      aria-label="Next image"
      class="carousel-control carousel-control-next absolute bg-white flex items-center justify-center rounded-full"
      :class="{ 'carousel-control-hidden' : currentImageIndex === (images.length - 1) }"
      :tabindex="currentImageIndex === (images.length - 1) ? '-1' : '0'"
      @click="next()"
    >
      <MajorityUiMajorityIcon
        class="leading-0"
        icon="chevron-right"
      />
    </button>
  </div>
</template>

<script>
import { v1 as uuidv1 } from 'uuid'
import { optimize } from '@/utils/image-optimization'

export default {
  props: {
    images: {
      type: Array,
      required: true
    },
    showControls: {
      type: Boolean,
      default: true
    },
    isFirst: {
      type: Boolean,
      default: false
    }
  },
  setup () {
    const { $device } = useNuxtApp()
    return $device
  },
  data () {
    return {
      safeId: uuidv1(),
      currentImageIndex: 0,
      observer: null
    }
  },
  computed: {
    isScrollable () {
      return this.images.length > 1
    },
    getOptmizeOptions () {
      const imgWidth = this.$device.isMobile ? { w: 430, h: 288, q: 40 } : { w: 660, h: 440 }
      return imgWidth
    }
  },
  mounted () {
    if (this.isScrollable) {
      const imageElements = document.querySelectorAll(`#carousel_${this.safeId} > li`)
      this.observer = new IntersectionObserver(this.onElementObserved, { root: this.$refs.carousel, threshold: 0.6 })

      if (imageElements.length > 1) {
        imageElements.forEach((image) => {
          this.observer.observe(image)
        })
      }
    }
  },
  beforeUnmount () {
    if (this.observer) {
      this.observer.disconnect()
    }
  },
  methods: {
    previous () {
      this.currentImageIndex--

      const carousel = this.$refs.carousel
      const amountToScroll = carousel.getBoundingClientRect().width * this.currentImageIndex
      this.$refs.carousel.scrollTo({ left: amountToScroll, behavior: 'smooth' })
    },
    optimiseImg (url) {
      return optimize(url, false, this.getOptmizeOptions)
    },
    next () {
      this.currentImageIndex++

      const carousel = this.$refs.carousel
      const amountToScroll = carousel.getBoundingClientRect().width * this.currentImageIndex
      this.$refs.carousel.scrollTo({ left: amountToScroll, behavior: 'smooth' })
    },
    onElementObserved (entries) {
      entries.forEach(({ target, isIntersecting }) => {
        if (!isIntersecting) {
          return
        }

        this.currentImageIndex = parseInt(target.getAttribute('data-index'))
      })
    }
  }
}
</script>

<style scoped lang="postcss">

  .carousel-scrollable {
    scrollbar-width: none;
    scroll-snap-type: x mandatory;
    &::-webkit-scrollbar {
      display: none;
    }
  }

  .carousel-control {
    top: 50%;
    left: 1rem;
    transform: translateY(-50%);
    height: 32px;
    width: 32px;
    opacity: 0.8;
    transition: opacity .2s ease-out;
    &:focus {
      outline: none;
    }
    &:focus-visible {
      opacity: 1;
      outline: none;
    }
  }

  .carousel-control-hidden {
    opacity: 0;
    pointer-events: none;
  }

  .carousel-control-next {
    left: auto;
    right: 1rem;
  }

  .carousel-image {
    scroll-snap-align: start;
  }

  @screen md {

    .carousel-control {
      width: 48px;
      height: 48px;
    }
  }
</style>
